import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import { getClassroomEmbedAuth, openClassroomZoomUrl, endSessionClassroom } from '../../api/classroom';
import { getApiErrorInfo, safeUserMessage } from '../../utils/apiError';
import { userDisplayName } from '../../utils/displayNames';
import {
  attachZoomClassroomRoot,
  detachZoomClassroomRoot,
  getZoomClassroomClient,
  joinZoomClassroom,
  leaveZoomClassroom,
} from '../../utils/zoomClassroomSession';

function zoomErrorText(err) {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return String(err.reason || err.message || err.errorMessage || err.type || '');
}

function isWaitingForHostError(err) {
  const msg = zoomErrorText(err).toLowerCase();
  return /not started|waiting for (the )?host|host has not|join before host/i.test(msg);
}

function isOnHoldUser(user) {
  if (!user || typeof user !== 'object') return false;
  return Boolean(user.bHold || user.isHold || user.onHold || user.isInWaitingRoom || user.bInWaitingRoom);
}

/** True when the payload explicitly includes a hold/waiting-room flag (partial user-updated must not clear). */
function hasExplicitHoldField(user) {
  if (!user || typeof user !== 'object') return false;
  return (
    Object.prototype.hasOwnProperty.call(user, 'bHold')
    || Object.prototype.hasOwnProperty.call(user, 'isHold')
    || Object.prototype.hasOwnProperty.call(user, 'onHold')
    || Object.prototype.hasOwnProperty.call(user, 'isInWaitingRoom')
    || Object.prototype.hasOwnProperty.call(user, 'bInWaitingRoom')
  );
}

function participantLabel(user, t) {
  return user.displayName || user.userName || userDisplayName(null, user.userId, t);
}

function readAttendees(client) {
  if (!client) return [];
  try {
    if (typeof client.getAttendeeslist === 'function') {
      return client.getAttendeeslist() || [];
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof client.getParticipantsList === 'function') {
      return client.getParticipantsList() || [];
    }
  } catch {
    /* ignore */
  }
  return [];
}

/**
 * Waiting-room users often never appear in getAttendeeslist (only via user-added with bHold).
 * Keep an event-sourced map and merge attendees when they do report hold.
 */
function collectWaitingParticipants(client, waitingMap) {
  const map = waitingMap || new Map();
  for (const u of readAttendees(client)) {
    if (u?.userId == null) continue;
    if (isOnHoldUser(u)) {
      map.set(u.userId, { userId: u.userId, displayName: participantLabel(u, t) });
    } else if (hasExplicitHoldField(u) && map.has(u.userId)) {
      map.delete(u.userId);
    }
  }
  return [...map.values()].filter((u) => u.userId != null);
}

async function readIsHost(client) {
  if (!client) return false;
  try {
    if (typeof client.isHost === 'function' && client.isHost()) return true;
  } catch {
    /* ignore */
  }
  try {
    const info = typeof client.getCurrentUserInfo === 'function' ? client.getCurrentUserInfo() : null;
    if (info?.isHost) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** After joining with ZAK, Zoom may need a moment (or reclaim) when someone joined first. */
async function ensureZoomHost(client, { attempts = 10, delayMs = 400 } = {}) {
  if (!client) return false;
  for (let i = 0; i < attempts; i += 1) {
    if (await readIsHost(client)) return true;
    try {
      if (typeof client.reclaimHost === 'function') {
        await Promise.resolve(client.reclaimHost());
      }
    } catch {
      /* ignore */
    }
    try {
      if (typeof client.claimHost === 'function') {
        await Promise.resolve(client.claimHost());
      }
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return readIsHost(client);
}

/**
 * Minimal in-app Zoom Meeting SDK room.
 * Hosts get an Admit banner for waiting-room students (Zoom's own WR UI is easy to miss).
 * Uses a persistent Zoom host node so Leave → Join Session works (SDK createClient is a singleton).
 */
export default function ClassroomMeetingPage({ role = 'ADMIN' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId: sessionIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const studentIdParam = searchParams.get('studentId');
  const studentId = studentIdParam && /^\d+$/.test(studentIdParam) ? Number(studentIdParam) : undefined;
  const asHostIntent = searchParams.get('host') === '1';

  const placeholderRef = useRef(null);
  const clientRef = useRef(null);
  const joinStartedRef = useRef(false);
  const joinGenerationRef = useRef(0);
  const leavingRef = useRef(false);
  /** @type {React.MutableRefObject<Map<number, { userId: number, displayName: string }>>} */
  const waitingMapRef = useRef(new Map());
  const hostListenersRef = useRef(null);
  const tRef = useRef(t);
  tRef.current = t;

  const [title, setTitle] = useState('');
  const [error, setError] = useState('');
  const [waitingHost, setWaitingHost] = useState(false);
  const [hostRetryNonce, setHostRetryNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isHost, setIsHost] = useState(false);
  const [waitingParticipants, setWaitingParticipants] = useState([]);
  const [admitting, setAdmitting] = useState(false);
  const [ending, setEnding] = useState(false);

  const leavePath = `/${String(role).toLowerCase()}/classroom/live`;

  const publishWaiting = useCallback(() => {
    const client = clientRef.current || getZoomClassroomClient();
    setWaitingParticipants(collectWaitingParticipants(client, waitingMapRef.current));
  }, []);

  const applyUserPayloads = useCallback((payload) => {
    const arr = Array.isArray(payload) ? payload : (payload ? [payload] : []);
    let changed = false;
    for (const u of arr) {
      if (!u || u.userId == null) continue;
      if (isOnHoldUser(u)) {
        waitingMapRef.current.set(u.userId, {
          userId: u.userId,
          displayName: participantLabel(u, t),
        });
        changed = true;
      } else if (hasExplicitHoldField(u) && waitingMapRef.current.has(u.userId)) {
        waitingMapRef.current.delete(u.userId);
        changed = true;
      }
    }
    if (changed) {
      publishWaiting();
    }
  }, [publishWaiting]);

  const onUsersRemoved = useCallback((payload) => {
    const arr = Array.isArray(payload) ? payload : (payload ? [payload] : []);
    let changed = false;
    for (const u of arr) {
      const id = u?.userId ?? u?.oderId;
      if (id == null) continue;
      if (waitingMapRef.current.delete(id)) {
        changed = true;
      }
    }
    if (changed) {
      publishWaiting();
    }
  }, [publishWaiting]);

  const refreshWaiting = useCallback(() => {
    publishWaiting();
  }, [publishWaiting]);

  const detachHostListeners = useCallback((client) => {
    const handlers = hostListenersRef.current;
    hostListenersRef.current = null;
    if (!client || !handlers || typeof client.off !== 'function') return;
    try {
      client.off('user-added', handlers.added);
      client.off('user-updated', handlers.updated);
      client.off('user-removed', handlers.removed);
    } catch {
      /* ignore */
    }
  }, []);

  const attachHostListeners = useCallback((client) => {
    if (!client || typeof client.on !== 'function') return;
    detachHostListeners(client);
    const handlers = {
      added: (payload) => applyUserPayloads(payload),
      updated: (payload) => applyUserPayloads(payload),
      removed: (payload) => onUsersRemoved(payload),
    };
    hostListenersRef.current = handlers;
    client.on('user-added', handlers.added);
    client.on('user-updated', handlers.updated);
    client.on('user-removed', handlers.removed);
  }, [applyUserPayloads, onUsersRemoved, detachHostListeners]);

  const leave = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    joinStartedRef.current = false;
    joinGenerationRef.current += 1;
    detachHostListeners(clientRef.current || getZoomClassroomClient());
    clientRef.current = null;
    waitingMapRef.current.clear();
    setWaitingParticipants([]);
    setIsHost(false);
    try {
      await leaveZoomClassroom();
    } finally {
      navigate(leavePath);
      leavingRef.current = false;
    }
  };

  const endClass = async () => {
    if (!sessionIdParam || ending) return;
    const ok = window.confirm(
      t('classroom.meeting.endHostConfirm', {
        defaultValue: 'End class for everyone? Students will be removed from the Zoom meeting.',
      }),
    );
    if (!ok) return;
    setEnding(true);
    setError('');
    try {
      await endSessionClassroom(sessionIdParam);
      await leave();
    } catch (err) {
      setEnding(false);
      setError(getApiErrorInfo(err, t('classroom.live.endFailed', { defaultValue: 'Could not end the class.' })).message);
    }
  };

  const admitAll = async () => {
    const client = clientRef.current;
    if (!client || admitting) return;
    setAdmitting(true);
    try {
      if (typeof client.admitAll === 'function') {
        await Promise.resolve(client.admitAll());
      } else {
        for (const p of [...waitingMapRef.current.values()]) {
          if (typeof client.admit === 'function') {
            await Promise.resolve(client.admit(p.userId));
          }
        }
      }
      waitingMapRef.current.clear();
      refreshWaiting();
    } catch (err) {
      setError(
        safeUserMessage(
          zoomErrorText(err),
          t('classroom.meeting.admitFailed', {
            defaultValue: 'Could not admit waiting students.',
          }),
        ),
      );
    } finally {
      setAdmitting(false);
      refreshWaiting();
    }
  };

  const admitOne = async (userId) => {
    const client = clientRef.current;
    if (!client || userId == null || admitting) return;
    setAdmitting(true);
    try {
      if (typeof client.admit === 'function') {
        await Promise.resolve(client.admit(userId));
      }
      waitingMapRef.current.delete(userId);
      refreshWaiting();
    } catch (err) {
      setError(
        safeUserMessage(
          zoomErrorText(err),
          t('classroom.meeting.admitFailed', {
            defaultValue: 'Could not admit waiting students.',
          }),
        ),
      );
    } finally {
      setAdmitting(false);
      refreshWaiting();
    }
  };

  useEffect(() => {
    const sessionId = Number(sessionIdParam);
    if (!Number.isFinite(sessionId) || sessionId <= 0) {
      setError(tRef.current('classroom.meeting.invalidSession', { defaultValue: 'Invalid session.' }));
      setLoading(false);
      return undefined;
    }

    if (joinStartedRef.current) {
      return undefined;
    }
    joinStartedRef.current = true;
    const generation = ++joinGenerationRef.current;
    let cancelled = false;
    let waitPoll = null;

    const isStale = () => cancelled || generation !== joinGenerationRef.current;

    (async () => {
      setLoading(true);
      setError('');
      setWaitingHost(false);
      setIsHost(false);
      waitingMapRef.current.clear();
      setWaitingParticipants([]);
      try {
        const auth = await getClassroomEmbedAuth(sessionId, {
          studentId: role === 'PARENT' ? studentId : undefined,
        });
        if (isStale()) return;
        setTitle(auth.batchName || tRef.current('classroom.live.unnamedSession', { defaultValue: 'Session' }));

        if (auth.provider === 'EXTERNAL_LINK' && auth.externalUrl) {
          openClassroomZoomUrl(auth.externalUrl);
          navigate(leavePath, { replace: true });
          return;
        }

        if (!auth.signature || !auth.sdkKey || !auth.meetingNumber) {
          throw new Error(
            tRef.current('classroom.meeting.embedAuthMissing', {
              defaultValue: 'Meeting credentials unavailable. Check Zoom Meeting SDK credentials under Classroom → Zoom.',
            }),
          );
        }

        const asHost = auth.role === 1;
        if (asHost && !auth.zak) {
          throw new Error(
            tRef.current('classroom.meeting.hostZakMissing', {
              defaultValue:
                'Host credentials missing. Set Classroom → Settings → Zoom host user email to a licensed Zoom user, then try again.',
            }),
          );
        }

        attachZoomClassroomRoot(placeholderRef.current);

        const client = await joinZoomClassroom({
          containerPlaceholder: placeholderRef.current,
          signature: auth.signature,
          sdkKey: auth.sdkKey,
          meetingNumber: auth.meetingNumber,
          password: auth.password || '',
          userName: auth.userName || t('classroom.meeting.guest', { defaultValue: 'Guest' }),
          userEmail: auth.userEmail || '',
          zak: asHost ? auth.zak : undefined,
          beforeJoin: asHost
            ? (c) => {
              // Attach before join so already-waiting students are not missed when staff joins later.
              clientRef.current = c;
              attachHostListeners(c);
            }
            : undefined,
        });
        if (isStale()) {
          await leaveZoomClassroom();
          return;
        }

        clientRef.current = client;

        let hostOk = asHost;
        if (asHost) {
          // Show admit UI as soon as role=host join succeeds; reclaim may take a moment.
          setIsHost(true);
          refreshWaiting();
          hostOk = await ensureZoomHost(client);
        } else {
          hostOk = false;
        }
        if (isStale()) {
          await leaveZoomClassroom();
          return;
        }
        setIsHost(hostOk);

        if (asHost && !hostOk) {
          setError(
            tRef.current('classroom.meeting.notActuallyHost', {
              defaultValue:
                'Joined, but Zoom did not grant host. Set Classroom → Settings → Zoom host user email to your licensed Zoom login, Leave, and Join again as coach.',
            }),
          );
        }

        if (hostOk) {
          try {
            if (typeof client.enableWaitingRoom === 'function') {
              await Promise.resolve(client.enableWaitingRoom(true));
            }
          } catch {
            /* meeting may already enforce WR from create settings */
          }
          if (!hostListenersRef.current) {
            attachHostListeners(client);
          }
          refreshWaiting();
          waitPoll = window.setInterval(refreshWaiting, 2000);
        } else if (asHost) {
          detachHostListeners(client);
          waitingMapRef.current.clear();
          setWaitingParticipants([]);
        }

        setLoading(false);
      } catch (err) {
        if (isStale()) return;
        joinStartedRef.current = false;
        setLoading(false);
        if (isWaitingForHostError(err)) {
          setWaitingHost(true);
          return;
        }
        const fallback = tRef.current('classroom.meeting.joinFailed', { defaultValue: 'Could not join the meeting.' });
        setError(safeUserMessage(zoomErrorText(err), getApiErrorInfo(err, fallback).message));
      }
    })();

    return () => {
      cancelled = true;
      joinGenerationRef.current += 1;
      if (waitPoll) {
        window.clearInterval(waitPoll);
      }
      const client = clientRef.current || getZoomClassroomClient();
      detachHostListeners(client);
      clientRef.current = null;
      waitingMapRef.current.clear();
      joinStartedRef.current = false;
      // Leave button already left; unmount (nav away) must leave + detach so rejoin can join() again.
      if (!leavingRef.current) {
        void leaveZoomClassroom();
      } else {
        detachZoomClassroomRoot();
      }
    };
  }, [
    sessionIdParam,
    role,
    studentId,
    leavePath,
    navigate,
    refreshWaiting,
    attachHostListeners,
    detachHostListeners,
    hostRetryNonce,
  ]);

  useEffect(() => {
    if (!waitingHost) return undefined;
    const timer = window.setInterval(() => {
      setHostRetryNonce((n) => n + 1);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [waitingHost]);

  const waitingCount = waitingParticipants.length;

  return (
    <div className="flex flex-col min-h-[70vh]">
      <div className="flex items-center justify-between gap-3 px-1 py-2 border-b border-slate-200">
        <h1 className="text-base font-semibold text-slate-900 truncate">
          {title || t('classroom.live.unnamedSession', { defaultValue: 'Session' })}
        </h1>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {(asHostIntent || isHost) && (
            <Button
              type="button"
              variant="page-secondary"
              disabled={ending}
              onClick={() => { void endClass(); }}
              data-classroom-end
            >
              {ending
                ? t('classroom.live.ending', { defaultValue: 'Ending…' })
                : t('classroom.live.end', { defaultValue: 'End class' })}
            </Button>
          )}
          <Button type="button" variant="page-secondary" onClick={() => { void leave(); }}>
            {t('classroom.meeting.leave', { defaultValue: 'Leave' })}
          </Button>
        </div>
      </div>
      {isHost && waitingCount > 0 && (
        <div
          className="m-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-3 text-sm text-sky-950"
          role="status"
          data-testid="classroom-waiting-room"
        >
          <p className="font-semibold">
            {t('classroom.meeting.waitingRoomTitle', {
              count: waitingCount,
              defaultValue: '{{count}} student(s) in the waiting room',
            }).replace('{{count}}', String(waitingCount))}
          </p>
          <ul className="mt-2 list-disc pl-5 space-y-1">
            {waitingParticipants.map((p) => (
              <li key={p.userId} className="flex flex-wrap items-center gap-2">
                <span className="text-sky-950">{p.displayName || t('classroom.meeting.waitingGuest', { defaultValue: 'Guest' })}</span>
                <button
                  type="button"
                  className="text-sky-800 font-semibold underline hover:no-underline disabled:opacity-50"
                  disabled={admitting}
                  onClick={() => { void admitOne(p.userId); }}
                >
                  {t('classroom.meeting.admitOne', { defaultValue: 'Admit' })}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-3">
            <Button type="button" variant="page-primary" disabled={admitting} onClick={() => { void admitAll(); }}>
              {admitting
                ? t('classroom.meeting.admitting', { defaultValue: 'Admitting…' })
                : t('classroom.meeting.admitAll', { defaultValue: 'Admit all' })}
            </Button>
          </div>
        </div>
      )}
      {waitingHost && !error && (
        <div className="m-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950" role="status">
          <p>
            {t('classroom.meeting.waitingForHost', {
              defaultValue: 'Waiting for your coach to start the class. Stay on this page — you will enter when they join.',
            })}
          </p>
          <p className="mt-1 text-amber-900/90">
            {t('classroom.meeting.waitingForHostAutoRetry', {
              defaultValue: 'We will retry automatically when your coach starts the class.',
            })}
          </p>
        </div>
      )}
      {error && (
        <div className="m-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
          {error}
          <div className="mt-2">
            <Button type="button" variant="page-secondary" onClick={() => { void leave(); }}>
              {t('common.back', { defaultValue: 'Back' })}
            </Button>
          </div>
        </div>
      )}
      {loading && !error && <PageLoadingShell label={t('classroom.live.starting', { defaultValue: 'Opening…' })} />}
      <div
        ref={placeholderRef}
        className="flex-1 min-h-[60vh] w-full bg-slate-900"
        data-testid="classroom-zoom-placeholder"
      />
    </div>
  );
}
